import { useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Minus,
  Plus,
  RotateCw,
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';

import { Button } from '@/components/ui/button';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

// Stable loading/error elements — defined outside the component so they never cause re-renders
const DOCUMENT_LOADING = (
  <div className="flex items-center justify-center py-12">
    <div className="flex flex-col items-center gap-2">
      <div className="h-6 w-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
      <p className="text-xs text-muted-foreground">Loading PDF…</p>
    </div>
  </div>
);

const DOCUMENT_ERROR = (
  <div className="flex items-center justify-center py-12">
    <p className="text-xs text-destructive">Failed to load PDF</p>
  </div>
);

const PAGE_LOADING = (
  <div className="flex items-center justify-center py-12">
    <div className="h-5 w-5 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
  </div>
);

type PdfViewerProps = {
  url: string;
};

export function PdfViewer({ url }: PdfViewerProps) {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  // Measure container width with ResizeObserver
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, numPages)));
  };

  const zoomIn = () => setScale(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  const zoomOut = () => setScale(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  const resetZoom = () => setScale(1);

  // Calculate page width to fit container
  const pageWidth = containerWidth ? (containerWidth - 32) * scale : undefined;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30 rounded-t-lg shrink-0">
        {/* Page navigation */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => goToPage(1)}
            disabled={currentPage <= 1}
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-muted-foreground px-2 min-w-[80px] text-center">
            {currentPage} / {numPages}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= numPages}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => goToPage(numPages)}
            disabled={currentPage >= numPages}
          >
            <ChevronsRight className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={zoomOut}
            disabled={scale <= MIN_ZOOM}
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <button
            onClick={resetZoom}
            className="text-xs text-muted-foreground px-1.5 min-w-[48px] text-center hover:text-foreground transition-colors"
          >
            {Math.round(scale * 100)}%
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={zoomIn}
            disabled={scale >= MAX_ZOOM}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
          <div className="w-px h-4 bg-border mx-1" />
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={resetZoom}>
            <RotateCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* PDF Content */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-muted/10 rounded-b-lg">
        <div className="flex justify-center p-4">
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={DOCUMENT_LOADING}
            error={DOCUMENT_ERROR}
          >
            <Page
              pageNumber={currentPage}
              width={pageWidth}
              renderAnnotationLayer
              renderTextLayer
              className="shadow-md rounded"
              loading={PAGE_LOADING}
            />
          </Document>
        </div>
      </div>
    </div>
  );
}
